/* eslint-disable i18next/no-literal-string -- later */
import { OrderType } from '@/affiliate-dashboard/graphql';
import { Button } from '@/components/base/button';
import { FormField } from '@/components/base/form/form-field';
import { Input } from '@/components/base/form/input';
import { PageHeading } from '@/components/base/page-heading';
import Section from '@/components/base/section';
import AppConfig from '@/global.config';
import useAffiliateCode from '@/hooks/use-affiliate-code';
import { faker } from '@faker-js/faker';
import { zodResolver } from '@hookform/resolvers/zod';
import { enqueueSnackbar } from 'notistack';
import { useState, type FormEvent } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

const userSchema = z.object({
  email: z.string().min(1, { message: 'Email is required' }).email(),
  amount: z.string().min(1, { message: 'Amount is required' }),
  currency: z.string().min(1, { message: 'Currency is required' }),
  orderNumber: z.string().min(1, { message: 'Order Number: is required' }),
  affiliateCode: z.string().optional(),
  password: z.string().min(1, { message: 'Password is required' }),
});

type User = z.infer<typeof userSchema>;

export function CreateOrder(): JSX.Element {
  const { affiliateCode } = useAffiliateCode();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const methods = useForm<User>({
    resolver: zodResolver(userSchema),
    mode: 'onChange',
    defaultValues: {
      email: faker.internet.email(),
      amount: faker.finance.amount({ max: 5000, dec: 0 }),
      currency: faker.finance.currencyCode(),
      affiliateCode: affiliateCode ?? '',
      orderNumber: faker.lorem.word(),
      password: 'monevis123',
    },
  });
  const { handleSubmit, reset } = methods;

  async function onSubmit(values: User): Promise<void> {
    try {
      setLoading(true);
      const response = await fetch(
        `https://partners-api.monevis.com/api/order?signature=${AppConfig.AFF_API_SIGN}`,
        {
          body: JSON.stringify({
            email: values.email,
            amount: values.amount,
            currency: values.currency,
            refCode: values.affiliateCode,
            orderNumber: values.orderNumber,
            orderType: OrderType.Order,
            createdAt: '2025-05-05T20:24:37.003836159Z',
            couponCode: '',
            lastName: 'Test',
            firstName: 'Dano',
          }),
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          (errorData as { message: string }).message ??
            `HTTP Error: ${response.status}`,
        );
      }
      const data: { orderNumber: string; refCode: string; email: string } =
        await response.json();
      enqueueSnackbar({
        message: `Order number:${data.orderNumber} Refcode:${data.refCode} with email ${data.email} created successfully.`,
        variant: 'success',
      });
      reset({
        orderNumber: faker.lorem.word(),
        amount: faker.finance.amount({ max: 5000, dec: 0 }),
      });
      setLoading(false);
    } catch (e) {
      if (e instanceof Error) {
        enqueueSnackbar({
          message: e.message,
          variant: 'error',
        });
      }
      setLoading(false);
    }
  }

  function handleClickSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    return void (async () => {
      handleSubmit(onSubmit)();
    })();
  }

  return (
    <Section variant="secondary">
      <div className="flex w-full flex-col">
        <PageHeading>Create Order</PageHeading>
        <PageHeading subheading="These data are random and can be modified" />

        <FormProvider {...methods}>
          <form onSubmit={handleClickSubmit} data-testid="register-form">
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-5">
                <div className="flex flex-col gap-5 sm:flex-row">
                  <fieldset className="w-full">
                    <FormField label={t('registration.email')} name="email">
                      <Input
                        variant="secondary"
                        data-testid="email-input"
                        placeholder={t('registration.emailPlaceholder')}
                      />
                    </FormField>
                  </fieldset>
                  <fieldset className="w-full">
                    <FormField
                      label={t('registration.password')}
                      name="password"
                    >
                      <Input variant="secondary" data-testid="password-input" />
                    </FormField>
                  </fieldset>
                </div>
                <div className="flex flex-col gap-5 sm:flex-row">
                  <fieldset className="w-full">
                    <FormField label="Amount" name="amount">
                      <Input type="number" variant="secondary" />
                    </FormField>
                  </fieldset>
                  <fieldset className="w-full">
                    <FormField label="Currency" name="currency">
                      <Input variant="secondary" />
                    </FormField>
                  </fieldset>
                </div>
                <div className="flex flex-col gap-5 sm:flex-row">
                  <fieldset className="w-full">
                    <FormField label="Ref code" name="affiliateCode">
                      <Input variant="secondary" />
                    </FormField>
                  </fieldset>
                  <fieldset className="w-full">
                    <FormField label="Order Number" name="orderNumber">
                      <Input variant="secondary" />
                    </FormField>
                  </fieldset>
                </div>
              </div>
              <Button
                as="button"
                className="text-base font-bold"
                loading={loading}
                type="submit"
                data-testid="registration-submit-button"
              >
                Create Order
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </Section>
  );
}
